// The simulation contract.
//
// Everything the DAEMON game is made of, described as plain data. No
// React, no DOM, no `Date`, no `Math.random` — a `SimState` is a value
// you can serialise, diff, snapshot and replay. The renderer reads it;
// the sim rewrites it; nothing else touches it.
//
// Coordinates are **logical units**, not pixels: the arena is
// ARENA_W × ARENA_H regardless of how big the window is, and the
// renderer scales. That matters for fairness — the old system used
// absolute CSS pixels for hit radii, so shrinking the window
// measurably changed the game.

import type { RngState } from "./rng";

// ── Constants ─────────────────────────────────────────────────────────

/** Simulation rate. Fixed. The renderer interpolates between ticks. */
export const TICK_HZ = 60;
export const TICK_MS = 1000 / TICK_HZ;

/** Logical arena. Square so that a window resize letterboxes rather
 *  than changing the playable shape. */
export const ARENA_W = 1000;
export const ARENA_H = 1000;

/** The player's hitbox radius, in logical units. Deliberately small —
 *  the drawn cursor is bigger, and the visible dot is the real hitbox
 *  so the player can see exactly what is being tested. */
export const PLAYER_R = 9;

/** No bullet may spawn within this radius of the player. Mandatory when
 *  the hitbox is the pointer: with no dash and no i-frames, a bullet
 *  spawned on top of you is unfair by construction. */
export const NO_SPAWN_R = 110;

/** Grazing range. Passing within this of a bullet's edge without being
 *  hit earns PRIV. Generous enough to be discoverable by accident. */
export const GRAZE_R = 34;

// ── Vectors ───────────────────────────────────────────────────────────

export type Vec = { x: number; y: number };

// ── Bullets ───────────────────────────────────────────────────────────

/** Visual/behavioural family. Drives colour and sprite in the renderer;
 *  the sim only uses it for damage lookup. */
export type BulletKind = "plain" | "heavy" | "homing" | "laser" | "child" | "heal";

export type Bullet = {
  id: number;
  x: number;
  y: number;
  /** Velocity in logical units **per tick** — not per second. Keeps the
   *  integrator a single add with no dt term to drift. */
  vx: number;
  vy: number;
  r: number;
  kind: BulletKind;
  damage: number;
  /** Tick this bullet was created, for age-based behaviour. */
  born: number;
  /** Ticks remaining before it despawns. */
  ttl: number;
  /** Turn rate in turns/tick, toward the player. 0 = straight. */
  homing: number;
  /** Multiplied into velocity each tick. 1 = constant speed. */
  accel: number;
  /** Times it may bounce off the arena edge before despawning. */
  bounces: number;
  /** Ticks to hold still before moving — the stop-and-go modifier. */
  delay: number;
  /** Has this bullet already awarded graze? One award each. */
  grazed: boolean;
};

/** A warning drawn before something becomes dangerous. Purely
 *  informational to the sim — it exists so the renderer can draw it and
 *  so fairness is auditable. */
export type Telegraph = {
  id: number;
  x: number;
  y: number;
  r: number;
  /** Ticks until the thing it warns about happens. */
  ticks: number;
  kind: "spawn" | "laser" | "slam";
};

// ── Enemies ───────────────────────────────────────────────────────────

/** A forked child process. Emits simpler patterns from its own origin.
 *  Killing one leaves a zombie until reaped. */
export type Child = {
  id: number;
  x: number;
  y: number;
  hp: number;
  hpMax: number;
  /** Index into the pattern table this child is running. */
  pattern: number;
  /** Ticks until its next emission step. */
  cooldown: number;
};

/** What a killed child leaves behind. Occupies space and blocks
 *  nothing — its danger is that reaping it drags you toward the fight. */
export type Zombie = {
  id: number;
  x: number;
  y: number;
  /** Ticks the cursor must dwell within reap range to clear it. */
  reap: number;
};

// ── The player ────────────────────────────────────────────────────────

export type Player = {
  x: number;
  y: number;
  hp: number;
  hpMax: number;
  /** Privilege. Earned by grazing, spent on signals, emptied on hit. */
  priv: number;
  privMax: number;
  /** Ticks of invulnerability remaining after a hit. */
  iframes: number;
  /**
   * Load average, in thousandths — 1000 is `load 1.00`.
   *
   * Named for what `uptime` reports, and measuring the same thing: how
   * much pressure the process is under. Grazing raises it, every tick
   * without a graze bleeds it back down, and a hit collapses it to
   * base. Held as an integer on purpose — the sim must reproduce
   * exactly on any engine, and integer arithmetic cannot accumulate the
   * way repeated float addition can.
   */
  loadMilli: number;
  /** Per-signal cooldowns, in ticks, indexed by SignalId order. */
  cooldowns: number[];
};

// ── Signals — the player's verbs ──────────────────────────────────────

export type SignalId = "sigterm" | "sigstop" | "sigkill" | "renice" | "strace";

export type SignalDef = {
  id: SignalId;
  name: string;
  /** PRIV to fire. SIGTERM is free so there is always something to do. */
  cost: number;
  /** Ticks before it can fire again. */
  cooldown: number;
  /** One-line description, shown in the HUD and the draft screen. */
  blurb: string;
};

// ── Spell cards ───────────────────────────────────────────────────────

/** A named, bounded attack. Touhou's structure: each has its own HP
 *  segment and its own timer, and ends when either runs out. This is
 *  what makes the fight a playlist of memorable objects rather than a
 *  smear of bullets, and it gives speedruns natural split points. */
export type SpellCard = {
  id: string;
  name: string;
  /** Fraction of DAEMON's phase HP this card represents. */
  hpShare: number;
  /** Ticks before the card times out and the next begins. */
  timeout: number;
  /** Pattern id, e.g. "sigpipe". Deliberately an id and not an index:
   *  a positional reference silently rebinds every card in the game the
   *  moment somebody reorders or inserts a row in the pattern table,
   *  and the failure is invisible — the fight simply becomes wrong. */
  pattern: string;
};

// ── Phases ────────────────────────────────────────────────────────────

export type PhaseId = "rl5" | "rl4" | "rl3" | "rl2" | "rl1";

export type Phase = {
  id: PhaseId;
  /** Shown on the phase card. `nice` level DAEMON has renice'd itself to. */
  nice: number;
  name: string;
  /** Candidate spell-card sets. One set is chosen per run by seed —
   *  fixed and learnable within a run, varied across runs. */
  cardSets: SpellCard[][];
  /** Device introduced by this phase, for the renderer and the HUD. */
  device: "none" | "handlers" | "fork" | "shrink" | "single-user";
};

// ── Run configuration ─────────────────────────────────────────────────

/** Difficulty, expressed the only way it should be. Lower is meaner. */
export type NiceLevel = 19 | 10 | 5 | 0 | -5 | -10 | -15 | -20;

export type RunModifier = {
  id: string;
  /** Rendered as a command-line flag on the start screen. */
  flag: string;
  blurb: string;
};

export type RunConfig = {
  seed: string;
  nice: NiceLevel;
  modifier: RunModifier | null;
  /** True for the daily seed, which is recorded separately. */
  daily: boolean;
};

// ── Upgrades ──────────────────────────────────────────────────────────

export type UpgradeArchetype = "aggression" | "control" | "greed" | "survival" | "chaos";
export type UpgradeRarity = "common" | "uncommon" | "rare";

export type Upgrade = {
  id: string;
  name: string;
  archetype: UpgradeArchetype;
  rarity: UpgradeRarity;
  blurb: string;
  /** Ids this one combines with, used to weight offers toward a build. */
  synergy: string[];
};

// ── Events — the sim's output channel ─────────────────────────────────
//
// The sim never plays a sound, shakes a screen or speaks a line. It
// appends events, and the presentation layer drains them each frame.
// That keeps `step()` pure and makes every effect replayable.

export type SimEvent =
  | { t: "graze"; x: number; y: number }
  | { t: "player-hit"; x: number; y: number; damage: number }
  | { t: "player-down" }
  | { t: "daemon-hit"; x: number; y: number; damage: number; crit: boolean }
  | { t: "signal"; id: SignalId; ok: boolean }
  | { t: "card-start"; name: string }
  | { t: "card-clear"; name: string; ticks: number }
  | { t: "phase-start"; phase: PhaseId; nice: number }
  | { t: "phase-clear"; phase: PhaseId; ticks: number }
  | { t: "fork"; count: number }
  | { t: "child-down"; x: number; y: number }
  | { t: "zombie-reaped"; x: number; y: number }
  | { t: "shrink"; scale: number }
  | { t: "won"; ticks: number }
  | { t: "lost"; ticks: number };

// ── The whole state ───────────────────────────────────────────────────

export type RunStatus = "intro" | "running" | "draft" | "won" | "lost";

export type SimState = {
  /** Ticks elapsed. This is the run timer — IGT, immune to frame drops
   *  and background-tab throttling, and the only clock that exists. */
  tick: number;
  status: RunStatus;
  rng: RngState;
  config: RunConfig;

  player: Player;

  daemon: {
    x: number;
    y: number;
    hp: number;
    hpMax: number;
    /** Vestigial. The parent's emissions run off `patternClock`, not
     *  this — kept only so old saved states still parse. */
    cooldown: number;
    /** The clock patterns are stepped against. Normally advances one
     *  per tick; `renice` advances it every OTHER tick, which is what
     *  halving an attack cadence actually means. Separate from
     *  `tick` so slowing the fight cannot slow the run timer. */
    patternClock: number;
    /** Ticks of `renice` remaining. */
    renicedFor: number;
    /** Step index within the running pattern. */
    step: number;
    /** True while a handler window makes SIGTERM bounce off. */
    guarded: boolean;
  };

  phaseIndex: number;
  /** Card sets chosen for this run, one per phase, picked by seed. */
  cardPlan: SpellCard[][];
  cardIndex: number;
  /** Ticks remaining on the current spell card. */
  cardTicks: number;

  bullets: Bullet[];
  telegraphs: Telegraph[];
  children: Child[];
  zombies: Zombie[];

  /** Arena scale, 1 = full. The shrink device drives this, and the
   *  renderer animates the real window frame to match. */
  arenaScale: number;

  upgrades: string[];
  /** Offers on the current draft screen, empty unless status is draft. */
  offers: string[];

  /** Per-run counters. Not persisted; the results screen reads them. */
  stats: {
    grazes: number;
    hitsTaken: number;
    damageDealt: number;
    cardsCleared: number;
    zombiesReaped: number;
    signalsFired: number;
    /** SIGTERMs specifically, separate from the total. The upgrade
     *  pipeline needs it — `sigqueue(3)` promotes every fifth SIGTERM to
     *  a SIGKILL, which is uncountable from the aggregate. */
    sigtermsFired: number;
    /**
     * Points. Earned only by being in danger and staying there — see
     * the load-average block in sim.ts.
     *
     * Deliberately not a function of time, so it does not quietly
     * become a second speedrun metric. A run is ranked on time or on
     * score, and the two want opposite things from the same decision.
     */
    score: number;
    /** Highest load reached. The run's peak nerve, kept because the
     *  final load says nothing — a hit on the last card would erase an
     *  otherwise flawless run's whole story. */
    peakLoadMilli: number;
  };

  /** Drained by the presentation layer every frame. */
  events: SimEvent[];

  /** Monotonic id source, so ids are replay-stable. */
  nextId: number;
};

// ── Input ─────────────────────────────────────────────────────────────

/** One tick of player intent. This is the entire replay format: record
 *  a stream of these alongside the seed and you can reproduce the run
 *  exactly. Cursor position is in logical arena units. */
export type SimInput = {
  x: number;
  y: number;
  /** True on the tick the button went down, not while held. */
  fire: boolean;
  /** Signals pressed this tick, in SignalId order. */
  signals: SignalId[];
};

export const EMPTY_INPUT: SimInput = { x: ARENA_W / 2, y: ARENA_H * 0.75, fire: false, signals: [] };
